/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import React from "react";
import { DropEvent, ErrorCode, FileRejection, useDropzone } from "react-dropzone";
import { Chip, Link, Stack, Typography } from "@mui/material";
import clsx from "clsx";
import { CloudArrowUpIcon } from "../../FontAwesome";
import { translationKeys, useTranslation } from "../../Internationalisierung/i18nBasis";
import FileUploadBaseProps from "./FileUploadBaseProps";
import { toAccept, toFile, toFileUpload } from "./utils";
import "./FileUploadBase.scss";
import variablesScss from "../../Themes/variables.module.scss";

export default function FileUploadBase(props: FileUploadBaseProps) {
    const { t } = useTranslation();
    const files = props.value.map(toFile);
    const [active, setActive] = React.useState<"active" | null>(null);

    const { getRootProps, getInputProps, inputRef } = useDropzone({
        multiple: props.multiple || (props.maxFiles !== undefined && props.maxFiles > 1),
        onDrop: setActive.bind(null, null),
        onDropAccepted: handleDropAccepted,
        onDropRejected: handleDropRejected,
        accept: toAccept(props.allowedFileExtensions),
        maxFiles: props.maxFiles,
        maxSize: props.maxFileSize,
        onDragOver: setActive.bind(null, "active"),
        onDragLeave: setActive.bind(null, null),
        disabled: props.readonly,
    });

    const isInitialized = React.useRef(false);
    React.useLayoutEffect(() => {
        if (!isInitialized.current && files.length > 0 && inputRef.current) {
            // Initialisieren des Werts eines File-Inputs: https://pqina.nl/blog/set-value-to-file-input/
            const dataTransfer = files.reduce((dataTransfer, file) => {
                dataTransfer.items.add(file);
                return dataTransfer;
            }, new DataTransfer());

            inputRef.current.files = dataTransfer.files;
            isInitialized.current = false;
        }
    }, [files]);

    async function handleDropAccepted(files: File[]) {
        if (props.multiple) {
            if (props.maxFiles !== undefined) {
                const filesCount = props.value.length + files.length + (props.chips?.length ?? 0)
                if (filesCount > Number(props.maxFiles)) {
                    const messages = [t(translationKeys.fileUploadBase.zuVieleDateienFehler, { count: props.maxFiles })];
                    props.onValidationError(messages[0], messages);
                    return;
                }
            }

            props.onChange(props.value.concat(await Promise.all(files.map(toFileUpload))));
        } else {
            props.onChange(await Promise.all(files.map(toFileUpload)));
        }
    }

    function handleDropRejected(fileRejections: FileRejection[], evt: DropEvent) {
        const messages = new Set<string>();
        fileRejections.forEach(fileRejection => {
            fileRejection.errors.forEach(err => {
                switch (err.code) {
                    case ErrorCode.FileTooLarge:
                        const megabytes = props.maxFileSize / (1024 * 1024);
                        messages.add(`${fileRejection.file.name}: ${t(translationKeys.fileUploadBase.zuGrosseDatei)} (${t(translationKeys.fileUploadBase.maximaleDateigroesse)}${megabytes} MB)`);
                        break;
                    case ErrorCode.FileInvalidType:
                        const dateiendungen = props.allowedFileExtensions.join(", ");
                        messages.add(`${fileRejection.file.name}: ${t(translationKeys.fileUploadBase.falscheDateiendung)} (${t(translationKeys.fileUploadBase.erlaubteDateiendungen)}${dateiendungen})`);
                        break;
                    case ErrorCode.TooManyFiles:
                        messages.add(t(translationKeys.fileUploadBase.zuVieleDateienFehler, { count: props.maxFiles }));
                        break;
                    default:
                        messages.add(`${fileRejection.file.name}: [${err.code}] ${err.message}`);
                        break;
                }
            })
        });

        const messagesArray = Array.from(messages);
        const message = messagesArray.join(" ");
        props.onValidationError(message, messagesArray);
    }

    return (
        <Stack component="section" gap={1} m={props.noMargin ? 0 : 1}>
            <div {...getRootProps({ className: clsx("lan-fileuploadbase__dropzone", active, props.thin && "thin", props.readonly && "disabled") })}>
                <input {...getInputProps()} />
                <Typography
                    sx={(theme) => ({
                        textAlign: "center",
                        fontStyle: "italic",
                        color: variablesScss.colorsBlack54,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: props.thin ? "row" : "column",
                        gap: props.thin ? theme.spacing(2) : 0,
                        minHeight: props.thin ? "3rem" : 0
                    })}
                >
                    {props.maxFiles === 1 ? t(translationKeys.fileUploadBase.dateiauswahl) : t(translationKeys.fileUploadBase.dateiauswahlPlural)}
                    <br />
                    <CloudArrowUpIcon fontSize="large" />
                </Typography>
            </div>
            <Stack component="aside" direction="row" gap={0.5} flexWrap="wrap">
                {!props.hideChips && ((props.chips ?? [])
                    .concat(files.map(file => ({
                        caption: file.name,
                        onDelete: () => {
                            if (inputRef.current?.files) {
                                const filesToKeep = props.value.filter(f => f.name !== file.name);
                                props.onChange(filesToKeep);
                            }
                        },
                    })))
                    .map((chip, idx) => (
                        <Chip
                            key={"file" + idx}
                            variant="outlined"
                            size="small"
                            label={chip.dateiUrl ? <Link
                                color="secondary"
                                href={chip.dateiUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {chip.caption}
                            </Link> : chip.caption}
                            color={chip.dateiUrl ? "secondary" : "default"}
                            {...(props.readonly ? {} : { onDelete: chip.onDelete })}
                        />
                    ))
                )}
            </Stack>
        </Stack>
    );
}
